import { usePageTitle } from '../hooks/usePageTitle.js';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api, type ImportRow, type SessionRow, type EhrRow, type BuildRow, type ConflictRow } from '../lib/api.js';
import { isFileDropSession } from '../lib/session-ui.js';
import { LogStream, type LogLine } from '../components/LogStream.js';
import { PipelineStageView, type StageNode, type Stage } from '../components/PipelineStageView.js';
import { ScopingPanel } from '../components/ScopingPanel.js';
import { ImportDashboard } from '../components/ImportDashboard.js';
import { NetworkTab } from '../components/NetworkTab.js';
import { FlatFileSessionPanel } from '../components/FlatFileSessionPanel.js';
import { SessionTabs } from '../components/SessionTabs.js';

// ─── helpers ─────────────────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case 'running': return 'var(--accent-yellow)';
    case 'paused': return 'var(--accent-blue)';
    case 'aborted': return 'var(--text-dimmed)';
    case 'completed': return 'var(--accent-green)';
    case 'failed': return 'var(--accent-red)';
    case 'draft':
    case 'pending': return 'var(--accent-blue)';
    default: return 'var(--text-dimmed)';
  }
}

function severityColor(severity: string) {
  if (severity === 'critical' || severity === 'error') return { bg: 'var(--accent-red)22', text: 'var(--accent-red)', border: 'var(--accent-red)55' };
  if (severity === 'warning' || severity === 'warn') return { bg: 'var(--accent-orange)22', text: 'var(--accent-orange)', border: 'var(--accent-orange)55' };
  return { bg: 'var(--accent-yellow)22', text: 'var(--accent-yellow)', border: 'var(--accent-yellow)55' };
}

function duration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const ms = (end ? new Date(end) : new Date()).getTime() - new Date(start).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// ─── LiveLog (SSE) ────────────────────────────────────────────────────────────

function LiveLog({ sessionId }: { sessionId: string }) {
  const [logs, setLogs] = useState<LogLine[]>([]);
  const [stageState, setStageState] = useState<Record<Stage, { state: 'pending' | 'active' | 'done' | 'error'; count: number }>>({
    scrape: { state: 'pending', count: 0 },
    parse: { state: 'pending', count: 0 },
    canonicalize: { state: 'pending', count: 0 },
    export: { state: 'pending', count: 0 },
    verify: { state: 'pending', count: 0 },
  });
  const [connected, setConnected] = useState(false);
  const [activeStage, setActiveStage] = useState<Stage | null>(null);
  const [logFilter, setLogFilter] = useState('');

  useEffect(() => {
    const topics = [
      `session:${sessionId}`,
      ...(['scrape', 'parse', 'canonicalize', 'export', 'verify'] as Stage[]).map(
        (s) => `session:${sessionId}:pipeline:${s}`,
      ),
    ];
    const params = new URLSearchParams({ topics: topics.join(',') });
    const es = new EventSource(`/api/stream?${params}`);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    es.addEventListener('log', (ev: MessageEvent) => {
      try {
        const evt = JSON.parse(ev.data) as { seq: number; ts: string; payload: { severity: LogLine['severity']; message: string; component?: string; meta?: Record<string, unknown> } };
        setLogs((prev) => [...prev.slice(-2000), { id: evt.seq, ts: new Date(evt.ts).toISOString().slice(11, 23), severity: evt.payload.severity, message: evt.payload.message, component: evt.payload.component, meta: evt.payload.meta }]);
      } catch { /* ignore */ }
    });

    const update = (stage: Stage, patch: Partial<{ state: 'pending' | 'active' | 'done' | 'error'; count: number }>) =>
      setStageState((prev) => ({ ...prev, [stage]: { ...prev[stage], ...patch } }));

    es.addEventListener('stage.started', (ev: MessageEvent) => {
      try { const { payload } = JSON.parse(ev.data) as { payload: { stage: Stage } }; update(payload.stage, { state: 'active' }); setActiveStage(payload.stage); } catch { /* ignore */ }
    });
    es.addEventListener('stage.progress', (ev: MessageEvent) => {
      try { const { payload } = JSON.parse(ev.data) as { payload: { stage: Stage; completed: number } }; update(payload.stage, { count: payload.completed }); } catch { /* ignore */ }
    });
    es.addEventListener('stage.finished', (ev: MessageEvent) => {
      try { const { payload } = JSON.parse(ev.data) as { payload: { stage: Stage; status: 'done' | 'error' } }; update(payload.stage, { state: payload.status }); } catch { /* ignore */ }
    });

    return () => es.close();
  }, [sessionId]);

  const stageNodes: StageNode[] = useMemo(
    () => (['scrape', 'parse', 'canonicalize', 'export', 'verify'] as Stage[]).map((stage) => ({
      stage, state: stageState[stage].state, count: stageState[stage].count,
    })),
    [stageState],
  );

  const filteredLogs = useMemo(() => {
    if (!activeStage) return logs;
    return logs;
  }, [logs, activeStage]);

  return { stageNodes, logs: filteredLogs, connected, logFilter, setLogFilter, activeStage, setActiveStage };
}

// ─── SessionDetailPage ────────────────────────────────────────────────────────

function EhrSessionDetail({ sessionId }: { sessionId: string }) {
  usePageTitle('Session Detail');
  const navigate = useNavigate();

  const { data: sessionData, isLoading, refetch } = useQuery({
    queryKey: ['sessions', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 5_000,
  });
  const { data: importsData } = useQuery({
    queryKey: ['sessions', sessionId, 'imports'],
    queryFn: () => api.getSessionImports(sessionId),
    refetchInterval: 3_000,
  });
  const { data: conflictsData } = useQuery({
    queryKey: ['conflicts', 'session', sessionId],
    queryFn: () => api.listConflicts(),
    refetchInterval: 10_000,
  });
  const { data: ehrsData } = useQuery({ queryKey: ['ehrs'], queryFn: () => api.listEhrs() });
  const { data: buildsData } = useQuery({ queryKey: ['builds'], queryFn: () => api.listBuilds() });
  // Scope recipe — only meaningful when session.recipeId is set; 404 is normal otherwise.
  const { data: scopeRecipeData } = useQuery({
    queryKey: ['sessions', sessionId, 'scope-recipe'],
    queryFn: () => api.getScopeRecipe(sessionId).catch(() => null),
    refetchInterval: 5_000,
  });

  const { stageNodes, logs, connected, logFilter, setLogFilter } = LiveLog({ sessionId });

  // Session-scoped "Reveal PHI" toggle. Default off. Each flip is audit-logged
  // server-side via /api/sessions/:id/phi/toggle (added in Phase E). Operators
  // are HIPAA-bound, so we don't gate this behind re-auth or countdowns —
  // a single click switches the view + writes the audit row.
  const [revealPhi, setRevealPhi] = useState(false);
  // Default tab in the lower section: Records vs Network
  const [bottomTab, setBottomTab] = useState<'records' | 'network'>('records');

  const session: SessionRow | undefined = sessionData?.session;
  const imports: ImportRow[] = importsData?.imports ?? [];
  const allConflicts: ConflictRow[] = conflictsData?.conflicts ?? [];
  const sessionConflicts = allConflicts.filter((c) => c.sessionId === sessionId);
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];
  const builds: BuildRow[] = buildsData?.builds ?? [];

  if (isLoading) {
    return <div className="p-12 text-sm text-text-dimmed">Loading…</div>;
  }
  if (!session) {
    return <div className="p-12 text-sm text-text-dimmed">Session not found.</div>;
  }

  const build = builds.find((b) => b.id === session.buildId);
  const ehrSrc = session.sources.find((s) => s.kind === 'ehr');
  const ehr = ehrs.find((e) => e.id === session.ehrId || e.id === ehrSrc?.ehrId || e.slug === ehrSrc?.ehrSlug);

  const totalFetched = imports.reduce((n, r) => n + r.totalFetched, 0);
  const totalExpected = imports.reduce((n, r) => n + (r.totalExpected ?? 0), 0);
  const openConflicts = sessionConflicts.filter((c) => c.status === 'open').length;
  const elapsed = duration(session.startedAt, session.finishedAt);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Header ── */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6">
        <Link to="/imports" className="text-xs text-text-dimmed hover:text-text-secondary transition-colors">
          ← Imports
        </Link>
        <span className="text-text-dimmed">/</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text-primary truncate">
            {session.title ?? session.id.slice(0, 8)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              const next = !revealPhi;
              setRevealPhi(next);
              // Fire-and-forget audit log (POST /sessions/:id/phi/toggle,
              // scope.ts). Swallow errors so a logging hiccup never blocks the
              // operator's view toggle.
              void fetch(`/api/sessions/${session.id}/phi/toggle`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reveal: next }),
              }).catch(() => null);
            }}
            className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-widest"
            style={{
              background: revealPhi ? 'rgba(244,63,94,0.18)' : 'rgba(148,163,184,0.18)',
              color: revealPhi ? 'rgb(253,164,175)' : 'rgb(203,213,225)',
              border: `1px solid ${revealPhi ? 'rgba(244,63,94,0.55)' : 'rgba(148,163,184,0.45)'}`,
            }}
            title={revealPhi
              ? 'PHI is currently visible. Click to redact again. Each toggle is audit-logged.'
              : 'PHI is redacted. Click to reveal raw values. HIPAA: each toggle is audit-logged.'}
          >
            {revealPhi ? '◉ PHI Visible' : '◌ PHI Hidden'}
          </button>
          <span
            className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
            style={{ background: `${statusColor(session.status)}22`, color: statusColor(session.status), border: `1px solid ${statusColor(session.status)}55` }}
          >
            {session.status}
          </span>
        </div>
      </header>

      <div className="shrink-0 border-b border-border px-6 py-2">
        <SessionTabs sessionId={session.id} active="overview" />
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        <div className="flex flex-1 flex-col overflow-y-auto p-6 space-y-5 min-w-0">

          {/* Stat strip */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Build', value: build ? <Link to="/builds/$id" params={{ id: build.id }} className="hover:underline" style={{ color: 'var(--accent-blue)' }}>{build.displayName}</Link> : '—', sub: build?.namespace },
              { label: 'EHR', value: ehr?.displayName ?? (ehrSrc?.ehrSlug as string) ?? '—', link: ehr ? `/ehrs/${ehr.id}` : null },
              { label: 'Records', value: totalExpected > 0 ? `${totalFetched} / ${totalExpected}` : String(totalFetched) },
              { label: 'Elapsed', value: elapsed },
            ].map(({ label, value, sub, link }) => (
              <div
                key={label}
                className="glass rounded-xl px-4 py-3"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}
              >
                <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">{label}</div>
                {link ? (
                  <Link to="/ehrs/$id" params={{ id: ehr!.id }} className="text-sm font-semibold hover:underline" style={{ color: 'var(--accent-blue)' }}>
                    {value}
                  </Link>
                ) : (
                  <div className="text-sm font-semibold text-text-primary">{value}</div>
                )}
                {sub && <div className="text-xs text-text-dimmed font-mono mt-0.5 truncate">{sub}</div>}
              </div>
            ))}
          </div>

          {/* Scoping panel — shown while scoping is in progress OR a recipe is pending review.
              status='scoping': worker is actively running.
              status='draft' + recipe.status='pending_review': worker finished, awaiting operator review. */}
          {(session.status === 'scoping'
            || (session.recipeId && scopeRecipeData?.recipe.status === 'pending_review')) && (
            <ScopingPanel
              sessionId={session.id}
              rescopeReason={session.rescopeReason ?? null}
              onAccept={() => void refetch()}
              onReject={() => void refetch()}
            />
          )}

          {/* Draft warnings / actions */}
          {session.status === 'draft' && !session.recipeId && (
            <div
              className="rounded-xl px-4 py-3 text-sm flex items-center justify-between"
              style={{ background: 'var(--accent-yellow)18', border: '1px solid var(--accent-yellow)55', color: 'var(--accent-yellow)' }}
            >
              <span>No recipe — scope this EHR before starting import</span>
              <button
                type="button"
                onClick={() => {
                  api.scopeSession(session.id)
                    .then(() => void refetch())
                    .catch((err) => console.error(err));
                }}
                className="ml-4 rounded-lg px-3 py-1 text-xs font-semibold"
                style={{ background: 'var(--accent-yellow)33', border: '1px solid var(--accent-yellow)66' }}
              >
                Start Scoping
              </button>
            </div>
          )}

          {(session.status === 'draft' || session.status === 'failed') && session.rescopeReason && (
            <div
              className="rounded-xl px-4 py-3 text-sm flex items-center justify-between"
              style={{ background: 'var(--accent-orange)18', border: '1px solid var(--accent-orange)55', color: 'var(--accent-orange)' }}
            >
              <span>Scoping required: {session.rescopeReason.replace(/_/g, ' ')}</span>
              <button
                type="button"
                onClick={() => {
                  // Reset to draft first if we're coming from a failed session,
                  // since the /scope endpoint expects the session to be eligible.
                  api.scopeSession(session.id)
                    .then(() => void refetch())
                    .catch((err) => console.error(err));
                }}
                className="ml-4 rounded-lg px-3 py-1 text-xs font-semibold"
                style={{ background: 'var(--accent-orange)33', border: '1px solid var(--accent-orange)66' }}
              >
                Rescope
              </button>
            </div>
          )}

          {(session.status === 'aborted' || session.status === 'completed') && session.recipeId && (
            <div className="flex">
              <button
                type="button"
                onClick={() => {
                  // Reset to draft first, then start. The API guards against
                  // double-start by only accepting a draft session.
                  fetch(`/api/sessions/${session.id}/reset`, { method: 'POST', credentials: 'include' })
                    .then(() => api.startSession(session.id))
                    .then(() => void refetch())
                    .catch((err) => toast.error(`Restart failed: ${err instanceof Error ? err.message : String(err)}`));
                }}
                className="rounded-xl px-4 py-2 text-sm font-semibold"
                style={{ background: 'rgba(56,189,248,0.18)', color: 'rgb(125,211,252)', border: '1px solid rgba(56,189,248,0.45)' }}
              >
                ▶ {session.status === 'aborted' ? 'Restart import' : 'Run again'}
              </button>
            </div>
          )}

          {session.status === 'draft' && session.recipeId && !session.rescopeReason
            && scopeRecipeData?.recipe.status === 'accepted' && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  api.startSession(session.id)
                    .then(() => void refetch())
                    .catch((err) => {
                      const msg = err instanceof Error ? err.message : String(err);
                      if (msg.includes('stale_recipe')) {
                        toast.error('Recipe is stale — please rescope before importing');
                      } else {
                        toast.error(`Start failed: ${msg}`);
                      }
                    });
                }}
                className="rounded-xl px-5 py-2 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }}
              >
                ▶ Start Import
              </button>
              <Link
                to="/sessions/$id/cache"
                params={{ id: session.id }}
                className="rounded-xl px-5 py-2 text-sm font-semibold"
                style={{ background: 'rgba(56,189,248,0.18)', color: 'rgb(125,211,252)', border: '1px solid rgba(56,189,248,0.45)' }}
              >
                View Imported Records
              </Link>
            </div>
          )}

          {/* Live import dashboard — shown for any active or recently-finished
              import. Includes pause/resume/abort controls, per-endpoint cards
              with parent/child indentation, and inline drill-down to recent
              HTTP requests per endpoint. */}
          {['running', 'paused', 'aborted', 'completed', 'failed'].includes(session.status) && (
            <ImportDashboard
              sessionId={session.id}
              status={session.status}
              warningCount={session.warningCount ?? 0}
              recipeYaml={scopeRecipeData?.recipe.yaml ?? null}
              onControlChange={() => void refetch()}
            />
          )}

          {/* Live log — visible immediately when the page loads, no scrolling
              required. Mirrors EVERY worker/agent log line (scope, cache,
              canonical, auth, safety) so operators can see exactly what's
              happening in real time. Auto-scrolls (toggle in header). */}
          {(['running', 'paused', 'scoping', 'aborted', 'completed', 'failed'].includes(session.status)
            || logs.length > 0) && (
            <div
              className="glass rounded-2xl overflow-hidden flex flex-col"
              style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)', height: '480px', minHeight: '480px' }}
            >
              <div className="flex shrink-0 items-center justify-between px-5 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Live log</div>
                  <span className="flex items-center gap-1.5 text-xs font-mono" style={{ color: connected ? 'var(--accent-green)' : 'var(--accent-yellow)' }}>
                    <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'animate-pulse' : ''}`} style={{ background: connected ? 'var(--accent-green)' : 'var(--accent-yellow)' }} />
                    {connected ? 'live' : 'reconnecting'}
                  </span>
                  <span className="text-xs text-text-dimmed font-mono">{logs.length.toLocaleString()} lines</span>
                </div>
                <input
                  type="text"
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  placeholder="Filter…"
                  className="rounded-lg px-2 py-1 text-xs font-mono outline-none w-40"
                  style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="flex-1 min-h-0">
                <LogStream lines={logs} filter={logFilter} />
              </div>
            </div>
          )}

          {/* Records / Network tab switcher — only meaningful once an import
              has produced data. Records shows the per-endpoint summary table
              and links to the cache browser; Network shows browser-devtools-
              style HTTP transparency for every fetch the cache layer made. */}
          {['running', 'paused', 'aborted', 'completed', 'failed'].includes(session.status) && (
            <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
              <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-border">
                {(['records', 'network'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setBottomTab(t)}
                    className="text-xs uppercase tracking-widest font-mono px-3 py-1.5 rounded"
                    style={{
                      background: bottomTab === t ? 'rgba(56,189,248,0.18)' : 'transparent',
                      color: bottomTab === t ? 'rgb(125,211,252)' : 'var(--text-dimmed)',
                      border: bottomTab === t ? '1px solid rgba(56,189,248,0.45)' : '1px solid transparent',
                    }}
                  >
                    {t === 'records' ? 'Records' : 'Network'}
                  </button>
                ))}
                <div className="ml-auto text-xs text-text-dimmed">
                  {bottomTab === 'records'
                    ? 'Per-endpoint row counts and links to inspect cached records.'
                    : 'Every HTTP request the cache layer made. Click any row for full wire details.'}
                </div>
              </div>
              <div className="p-4">
                {bottomTab === 'network' && (
                  <NetworkTab
                    sessionId={session.id}
                    isLive={session.status === 'running' || session.status === 'paused'}
                    reveal={revealPhi}
                  />
                )}
                {bottomTab === 'records' && imports.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary">Endpoint</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary">Status</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">Fetched</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">Expected</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {imports.map((r) => (
                        <tr key={r.id} className="border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100">
                          <td className="px-3 py-2 font-mono text-xs text-text-secondary">{r.endpoint}</td>
                          <td className="px-3 py-2">
                            <span className="rounded px-1.5 py-0.5 text-xs font-semibold"
                                  style={{ background: `${statusColor(r.status)}22`, color: statusColor(r.status) }}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-text-secondary">{r.totalFetched}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-text-dimmed">{r.totalExpected ?? '—'}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-text-dimmed">{new Date(r.createdAt).toLocaleTimeString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {bottomTab === 'records' && imports.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-text-dimmed italic">
                    No import rows yet.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Completed banner + view-cache link */}
          {session.status === 'completed' && (
            <div
              className="rounded-xl p-4 flex items-center justify-between"
              style={{ background: 'rgba(13,207,184,0.10)', border: '1px solid rgba(13,207,184,0.4)' }}
            >
              <div>
                <div className="text-sm font-semibold" style={{ color: 'rgb(94,234,212)' }}>
                  ✓ Import complete
                </div>
                {session.finishedAt && (
                  <div className="text-xs text-text-dimmed mt-0.5">
                    Finished {new Date(session.finishedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <Link
                to="/sessions/$id/cache"
                params={{ id: session.id }}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
              >
                View Imported Records →
              </Link>
            </div>
          )}

          {/* Pipeline */}
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Pipeline</div>
              <div className="text-xs text-text-dimmed font-mono">{session.id.slice(0, 8)}</div>
            </div>
            <PipelineStageView stages={stageNodes} />
          </div>

          {/* (Legacy "Import rows" table removed — its content is now in the
              Records / Network tab switcher above.) */}

          {/* Conflicts */}
          {sessionConflicts.length > 0 && (
            <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Conflicts</div>
                <div className="flex items-center gap-2">
                  {openConflicts > 0 && (
                    <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)' }}>
                      {openConflicts} open
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void navigate({ to: '/conflicts' })}
                    className="text-xs hover:underline"
                    style={{ color: 'var(--accent-blue)' }}
                  >
                    Manage all →
                  </button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-5 py-2 text-left text-xs font-semibold text-text-secondary">Kind</th>
                    <th className="px-5 py-2 text-left text-xs font-semibold text-text-secondary">Severity</th>
                    <th className="px-5 py-2 text-left text-xs font-semibold text-text-secondary">Context</th>
                    <th className="px-5 py-2 text-right text-xs font-semibold text-text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionConflicts.slice(0, 20).map((c) => {
                    const sc = severityColor(c.severity);
                    return (
                      <tr key={c.id} className="border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100">
                        <td className="px-5 py-2.5">
                          <span className="rounded border px-1.5 py-0.5 font-mono text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>{c.kind}</span>
                        </td>
                        <td className="px-5 py-2.5">
                          <span className="rounded-full px-2 py-0.5 text-xs font-semibold uppercase" style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>{c.severity}</span>
                        </td>
                        <td className="px-5 py-2.5 font-mono text-xs text-text-dimmed max-w-xs truncate">
                          {Object.entries(c.context ?? {}).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={c.status === 'resolved' ? { background: 'var(--accent-green)22', color: 'var(--accent-green)' } : { background: 'var(--accent-yellow)22', color: 'var(--accent-yellow)' }}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Metadata */}
          <div className="glass rounded-2xl px-5 py-4 text-xs space-y-2" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
            <div className="text-text-dimmed uppercase tracking-widest mb-2 text-xs font-semibold">Session metadata</div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-text-dimmed w-24 shrink-0">ID</span>
                <span className="font-mono text-text-secondary">{session.id}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-text-dimmed w-24 shrink-0">Status</span>
                <span className="font-mono" style={{ color: statusColor(session.status) }}>{session.status}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-text-dimmed w-24 shrink-0">Started</span>
                <span className="font-mono text-text-secondary">{session.startedAt ? new Date(session.startedAt).toLocaleString() : '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-text-dimmed w-24 shrink-0">Finished</span>
                <span className="font-mono text-text-secondary">{session.finishedAt ? new Date(session.finishedAt).toLocaleString() : '—'}</span>
              </div>
              {session.description && (
                <div className="flex items-start gap-2 col-span-2">
                  <span className="text-text-dimmed w-24 shrink-0">Notes</span>
                  <span className="text-text-secondary">{session.description}</span>
                </div>
              )}
            </div>
          </div>

          {/* (Live log relocated above — kept here as a no-op anchor) */}

        </div>
      </div>
    </div>
  );
}

// ─── Dispatcher: route CSV/bulk sessions to the flat-file layout ───────────────

export function SessionDetailPage({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['sessions', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 5_000,
  });
  const session = data?.session;
  if (isLoading) return <div className="p-12 text-sm text-text-dimmed">Loading…</div>;
  if (!session) return <div className="p-12 text-sm text-text-dimmed">Session not found.</div>;
  // File-based drops (flat file, spreadsheet, archive/DB dump, documents) → flat-file
  // layout (FhirImportPanel); a live-EHR-scrape session → EhrSessionDetail. Routing is
  // unit-tested in session-ui (a regression here silently hides the import controls).
  return isFileDropSession(session.sources)
    ? <BulkSessionDetail sessionId={sessionId} />
    : <EhrSessionDetail sessionId={sessionId} />;
}

// ─── BulkSessionDetail: the flat-file / bulk-dump session page ─────────────────

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-text-dimmed w-24 shrink-0">{label}</span>
      <span className={`text-text-secondary ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function BulkSessionDetail({ sessionId }: { sessionId: string }) {
  usePageTitle('Import — flat file');
  const { data: sessionData } = useQuery({
    queryKey: ['sessions', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 5_000,
  });
  const { data: buildsData } = useQuery({ queryKey: ['builds'], queryFn: () => api.listBuilds() });
  const { data: statsData } = useQuery({
    queryKey: ['stats', sessionId],
    queryFn: () => api.getImportStats(sessionId),
    refetchInterval: 4_000,
  });
  const [revealPhi, setRevealPhi] = useState(false);

  const session = sessionData?.session;
  if (!session) return <div className="p-12 text-sm text-text-dimmed">Loading…</div>;

  const build = (buildsData?.builds ?? []).find((b) => b.id === session.buildId);
  const csvSource = session.sources.find((s) => s.kind === 'csv' || s.kind === 'bulk');
  const sourceLabel = (csvSource?.sourceSystem as string) || (csvSource?.fileName as string) || 'flat file';
  const loaded = statsData?.stats?.rowsLoaded ?? 0;
  const elapsed = duration(session.startedAt, session.finishedAt);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6">
        <Link to="/imports" className="text-xs text-text-dimmed hover:text-text-secondary transition-colors">← Imports</Link>
        <span className="text-text-dimmed">/</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text-primary truncate">{session.title ?? session.id.slice(0, 8)}</div>
        </div>
        <button
          type="button"
          onClick={() => {
            const next = !revealPhi;
            setRevealPhi(next);
            // Fire-and-forget audit log (each PHI reveal/redact toggle is recorded).
            void fetch(`/api/sessions/${session.id}/phi/toggle`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ reveal: next }),
            }).catch(() => {});
          }}
          className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{
            background: revealPhi ? 'rgba(244,63,94,0.18)' : 'rgba(148,163,184,0.18)',
            color: revealPhi ? 'rgb(253,164,175)' : 'rgb(203,213,225)',
            border: `1px solid ${revealPhi ? 'rgba(244,63,94,0.55)' : 'rgba(148,163,184,0.45)'}`,
          }}
          title={revealPhi ? 'PHI is visible. Click to redact. HIPAA: each toggle is audit-logged.' : 'PHI is redacted. Click to reveal raw values. HIPAA: each toggle is audit-logged.'}
        >
          {revealPhi ? '◉ PHI Visible' : '◌ PHI Hidden'}
        </button>
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
          style={{ background: `${statusColor(session.status)}22`, color: statusColor(session.status), border: `1px solid ${statusColor(session.status)}55` }}
        >
          {session.status}
        </span>
      </header>

      <div className="shrink-0 border-b border-border px-6 py-2">
        <SessionTabs sessionId={session.id} active="overview" />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-y-auto p-6 space-y-5 min-w-0">
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Build', value: build ? <Link to="/builds/$id" params={{ id: build.id }} className="hover:underline" style={{ color: 'var(--accent-blue)' }}>{build.displayName}</Link> : '—' },
              { label: 'Source', value: sourceLabel },
              { label: 'Loaded', value: loaded.toLocaleString() },
              { label: 'Elapsed', value: elapsed },
            ].map(({ label, value }) => (
              <div key={label} className="glass rounded-xl px-4 py-3" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
                <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">{label}</div>
                <div className="text-sm font-semibold text-text-primary truncate">{value}</div>
              </div>
            ))}
          </div>

          <FlatFileSessionPanel session={session} reveal={revealPhi} />

          <div className="glass rounded-2xl px-5 py-4 text-xs space-y-2" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
            <div className="text-text-dimmed uppercase tracking-widest mb-2 text-xs font-semibold">Session metadata</div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
              <Meta label="ID" value={session.id} mono />
              <Meta label="Status" value={session.status} />
              <Meta label="Started" value={session.startedAt ? new Date(session.startedAt).toLocaleString() : '—'} />
              <Meta label="Finished" value={session.finishedAt ? new Date(session.finishedAt).toLocaleString() : '—'} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
